import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import { Button } from '@/shared/ui/Button/Button';
import { Input } from '@/shared/ui/Input/Input';
import { Select } from '@/shared/ui/Select/Select';
import { Modal } from '@/shared/ui/Modal/Modal';
import { Table } from '@/entities/Table';
import { alertOpen } from '@/entities/Alert';
import { useGetNewsListQuery, useDeleteNewsMutation, useUpdateNewsMutation, NewsItem } from '@/entities/News';
import { IconButton } from '@/shared/ui/IconButton/IconButton';
import { StatusBadge } from '@/shared/ui/StatusBadge/StatusBadge';
import { Loader } from '@/shared/ui/Loader/Loader';
import EditIcon from '@/shared/assets/icons/edit.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import SearchIcon from '@/shared/assets/icons/search.svg';
import RadarIcon from '@/shared/assets/icons/radar.svg';
import {
  categoryOptions,
  categoryFilterOptions,
  statusFilterOptions,
  roleFilterOptions,
  roleDetails,
  buildActivePinnedByRole,
  collectStalePins,
} from '../model/newsConstants';
import s from './NewsPage.module.scss';

export const NewsList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: listData, isLoading: isListLoading } = useGetNewsListQuery({
    category: categoryFilter,
    status: statusFilter,
    role: roleFilter,
    limit,
    offset: (page - 1) * limit,
  });

  const [deleteNews] = useDeleteNewsMutation();
  const [updateNews] = useUpdateNewsMutation();
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [stalePinsToUnpin, setStalePinsToUnpin] = useState<{ id: number; pinnedRoles: string[] }[]>([]);

  const newsList = useMemo(() => listData?.news ?? [], [listData]);

  const activePinnedByRole = useMemo(() => buildActivePinnedByRole(newsList), [newsList]);

  useEffect(() => {
    if (newsList.length === 0) return;
    const stale = collectStalePins(newsList, activePinnedByRole);
    if (stale.length > 0 && stalePinsToUnpin.length === 0) {
      setStalePinsToUnpin(stale);
    }
  }, [newsList, activePinnedByRole, stalePinsToUnpin.length]);

  useEffect(() => {
    if (stalePinsToUnpin.length === 0) return;
    const item = stalePinsToUnpin[0];
    updateNews({ id: item.id, pinnedRoles: item.pinnedRoles })
      .unwrap()
      .then(() => {
        setStalePinsToUnpin((prev) => prev.slice(1));
      })
      .catch(() => {
        setStalePinsToUnpin((prev) => prev.slice(1));
      });
  }, [stalePinsToUnpin, updateNews]);

  const filteredNews = useMemo(() => {
    if (!search.trim()) return newsList;
    const q = search.toLowerCase();
    return newsList.filter((item) => item.title.toLowerCase().includes(q));
  }, [newsList, search]);

  const handleCreateClick = () => {
    navigate('/news/create');
  };

  const handleEditClick = (item: NewsItem) => {
    navigate(`/news/${item.id}/edit`);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteId === null) return;
    try {
      await deleteNews(deleteId).unwrap();
      dispatch(
        alertOpen({
          content: { header: '', body: ['Новость успешно удалена'], footer: '' },
          isError: false,
        }),
      );
    } catch (err: any) {
      const msg = err?.data?.message || err?.message || 'Не удалось удалить новость';
      dispatch(
        alertOpen({
          content: { header: '', body: [msg], footer: '' },
          isError: true,
        }),
      );
    } finally {
      setDeleteId(null);
    }
  };

  const columns: ColumnDef<NewsItem>[] = [
    {
      id: 'cover',
      header: 'Обложка',
      meta: {
        thClassName: classNames(s.colCover, s.centerHeader),
        tdClassName: s.colCover,
      },
      cell: ({ row }) => {
        const url = row.original.coverImageUrl;
        return (
          <div className={s.coverCell}>
            {url ? (
              <img src={url} alt="Cover" className={s.thumbnail} />
            ) : (
              <div className={s.noThumbnail}>Нет фото</div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'title',
      header: 'Заголовок',
      meta: {
        thClassName: s.colTitle,
        tdClassName: s.colTitle,
      },
      cell: ({ row }) => (
        <div className={s.titleCellWrapper}>
          <div className={s.titleCell}>{row.original.title}</div>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Категория',
      meta: {
        thClassName: s.colCategory,
        tdClassName: s.colCategory,
      },
      cell: ({ row }) => {
        const cat = row.original.category;
        const label = categoryOptions.find((o) => o.value === cat)?.label || cat;
        return (
          <StatusBadge tone="gray" customIcon={null}>
            {label}
          </StatusBadge>
        );
      },
    },
    {
      accessorKey: 'targetRoles',
      header: 'Получатели',
      meta: {
        thClassName: s.colRoles,
        tdClassName: s.colRoles,
      },
      cell: ({ row }) => {
        const roles = row.original.targetRoles;
        const pinned = row.original.pinnedRoles || [];
        const now = new Date();
        return (
          <div className={s.rolesCell}>
            {roles.map((r) => {
              const label = roleDetails.find((d) => d.value === r)?.label || r;
              if (!pinned.includes(r)) {
                return (
                  <StatusBadge key={r} tone="gray" customIcon={null}>
                    {label}
                  </StatusBadge>
                );
              }
              const active = activePinnedByRole[r];
              const isActive = active?.id === row.original.id;
              const isFutureScheduled = Boolean(row.original.publishedAt && new Date(row.original.publishedAt) > now);
              const showPinned = isActive || isFutureScheduled;
              return (
                <StatusBadge
                  key={r}
                  tone={showPinned ? 'green' : 'gray'}
                  customIcon={showPinned ? <RadarIcon /> : null}
                >
                  {label}
                </StatusBadge>
              );
            })}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Статус',
      meta: {
        thClassName: s.colStatus,
        tdClassName: s.colStatus,
      },
      cell: ({ row }) => {
        const val = row.original.status;
        const isPublished = val === 'published';
        const publishedAt = row.original.publishedAt;
        const isScheduled = isPublished && publishedAt && new Date(publishedAt) > new Date();
        return (
          <div className={classNames(s.statusBadgeWrap, isScheduled && s.statusBadgeScheduled)}>
            <StatusBadge tone={isPublished ? 'green' : 'orange'} customIcon={null}>
              {isScheduled ? 'Запланировано' : isPublished ? 'Опубликовано' : 'Черновик'}
            </StatusBadge>
          </div>
        );
      },
    },
    {
      accessorKey: 'publishedAt',
      header: 'Дата публикации',
      meta: {
        thClassName: s.colDate,
        tdClassName: s.colDate,
      },
      cell: ({ row }) => (row.original.publishedAt ? new Date(row.original.publishedAt).toLocaleString() : '—'),
    },
    {
      id: 'actions',
      header: 'Действия',
      meta: {
        thClassName: s.colActions,
        tdClassName: s.colActions,
      },
      cell: ({ row }) => (
        <div className={s.actionsCell}>
          <IconButton size="s" color="black" onClick={() => handleEditClick(row.original)} title="Изменить">
            <EditIcon />
          </IconButton>
          <IconButton size="s" color="red" onClick={() => handleDeleteClick(row.original.id)} title="Удалить">
            <TrashIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={s.filterBar}>
        <div className={s.filtersGroup}>
          <div className={s.filterItem}>
            <Input
              value={search}
              onChange={(e: any) => setSearch(typeof e === 'string' ? e : e.target.value)}
              placeholder="Поиск по заголовку..."
              error=""
              className={s.searchField}
              icon={<SearchIcon />}
            />
          </div>
          <div className={s.filterItem}>
            <Select value={categoryFilter} options={categoryFilterOptions} onChange={setCategoryFilter} />
          </div>
          <div className={s.filterItem}>
            <Select value={statusFilter} options={statusFilterOptions} onChange={setStatusFilter} />
          </div>
          <div className={s.filterItem}>
            <Select value={roleFilter} options={roleFilterOptions} onChange={setRoleFilter} />
          </div>
        </div>
        <Button onClick={handleCreateClick} size="m" width="auto">
          Создать новость
        </Button>
      </div>

      <div className={s.tableWrapper}>
        {isListLoading ? (
          <div className={s.loadingBox}>
            <Loader className={s.loader} />
          </div>
        ) : (
          <Table columns={columns} data={filteredNews} onSelect={() => {}} tableRef={{ current: {} }} />
        )}
      </div>

      {deleteId !== null && (
        <Modal open={deleteId !== null} onClose={() => setDeleteId(null)} title="Удаление новости">
          <div className={s.modalContent}>
            Вы действительно хотите удалить эту новость?
            <br />
            Это действие необратимо.
          </div>
          <div className={s.modalActions}>
            <Button onClick={() => setDeleteId(null)} variant="outline" color="red-light" width="auto">
              Отмена
            </Button>
            <Button onClick={handleConfirmDelete} color="red" width="auto">
              Удалить
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
